import { Link } from 'react-router-dom'
import type { FeaturedExpert } from '../../types'

interface ExpertCardProps {
  expert: FeaturedExpert
  compact?: boolean
}

function formatRate(min?: number, max?: number, currency?: string): string {
  if (!min && !max) return ''
  const currencySymbol = currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : '$'
  if (min && max) {
    return `${currencySymbol}${min} - ${currencySymbol}${max}/hr`
  }
  return min ? `From ${currencySymbol}${min}/hr` : `Up to ${currencySymbol}${max}/hr`
}

export function ExpertCard({ expert, compact = false }: ExpertCardProps) {
  const displayName = expert.display_name || `${expert.first_name} ${expert.last_name.charAt(0)}.`
  const location = [expert.city, expert.country].filter(Boolean).join(', ')

  return (
    <Link
      to={`/experts/${expert.id}`}
      className="block bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow"
    >
      {/* Header with avatar and verification */}
      <div className="flex items-start gap-4 mb-4">
        <div className="relative">
          {expert.avatar_url ? (
            <img
              src={expert.avatar_url}
              alt={displayName}
              className="w-14 h-14 rounded-full object-cover border-2 border-gray-200"
            />
          ) : (
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-semibold text-lg">
              {expert.first_name.charAt(0)}{expert.last_name.charAt(0)}
            </div>
          )}
          {expert.is_verified && (
            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center border-2 border-white">
              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900 truncate">{displayName}</h3>
            {expert.is_verified && (
              <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">
                Verified
              </span>
            )}
          </div>
          {expert.headline && (
            <p className="text-sm text-gray-600 truncate">{expert.headline}</p>
          )}
        </div>
      </div>

      {/* Details */}
      <div className="space-y-2 mb-4">
        {/* Location */}
        {location && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span>{location}</span>
          </div>
        )}

        {/* Experience */}
        {expert.years_experience && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{expert.years_experience}+ years experience</span>
          </div>
        )}

        {/* Rate */}
        {(expert.hourly_rate_min || expert.hourly_rate_max) && (
          <div className="flex items-center gap-2 text-sm font-medium text-green-600">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{formatRate(expert.hourly_rate_min, expert.hourly_rate_max, expert.currency)}</span>
          </div>
        )}
      </div>

      {/* Skills */}
      {expert.skills && expert.skills.length > 0 && !compact && (
        <div className="flex flex-wrap gap-1.5">
          {expert.skills.slice(0, 4).map((skill) => (
            <span
              key={skill.name}
              className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded ${
                skill.is_verified
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {skill.is_verified && (
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
              {skill.name}
            </span>
          ))}
          {expert.skills.length > 4 && (
            <span className="text-xs text-gray-500">+{expert.skills.length - 4} more</span>
          )}
        </div>
      )}

      {/* Availability badge */}
      {expert.available_for_hire && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <span className="inline-flex items-center text-xs text-green-600">
            <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
            Available for hire
          </span>
        </div>
      )}
    </Link>
  )
}
